import { useLayoutEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { IconChevronDown, IconClose } from '../icons/index.js';
import { cn } from '../lib/cn.js';
import { Button } from './Button.js';
import { Input } from './Input.js';
import { searchField } from '../views/field-query.js';

export interface SortPopoverOption {
  key: string;
  label: string;
}

export interface SortPopoverLevel {
  key: string;
  dir: 'asc' | 'desc';
}

export interface SortPopoverProps {
  id: string;
  options: readonly SortPopoverOption[];
  stack: readonly SortPopoverLevel[];
  onToggle(key: string): void;
  onInvert(key: string): void;
  onRemove(key: string): void;
  onClear(): void;
}

/** Panneau de tri multi-critères, ancré par son parent dans la rangée d'outils. */
export function SortPopover({
  id,
  options,
  stack,
  onToggle,
  onInvert,
  onRemove,
  onClear,
}: SortPopoverProps): JSX.Element {
  const [search, setSearch] = useState('');
  const directionRefs = useRef(new Map<string, HTMLButtonElement>());
  const availableRefs = useRef(new Map<string, HTMLButtonElement>());
  const pendingFocus = useRef<
    { action: 'added'; key: string } | { action: 'removed'; key: string } | { action: 'cleared' } | null
  >(null);
  const catalogue = new Map<string, SortPopoverOption>();
  for (const option of options) {
    if (!catalogue.has(option.key)) catalogue.set(option.key, option);
  }

  const seen = new Set<string>();
  const active = stack.filter((level) => {
    if (seen.has(level.key)) return false;
    seen.add(level.key);
    return true;
  });
  const available = [...catalogue.values()].filter((option) => !seen.has(option.key) && searchField(option.label).includes(searchField(search)));

  useLayoutEffect(() => {
    const pending = pendingFocus.current;
    if (pending == null) return;
    pendingFocus.current = null;
    if (pending.action === 'added') {
      directionRefs.current.get(pending.key)?.focus();
      return;
    }
    const preferred = pending.action === 'removed' ? availableRefs.current.get(pending.key) : undefined;
    (preferred ?? availableRefs.current.get(available[0]?.key ?? ''))?.focus();
  }, [active, available]);

  const focus =
    'focus-visible:outline-none focus-visible:shadow-none focus-visible:ring-1 ' +
    'focus-visible:ring-inset focus-visible:ring-accent-border';

  return (
    <div
      id={id}
      role="region"
      aria-label="Trier par"
      className={cn(
        'absolute right-0 top-[calc(100%+0.4375rem)] z-20 flex max-h-[min(32rem,calc(100dvh-12rem))] w-[20rem] max-w-[calc(100vw-2rem)] flex-col',
        'overflow-hidden rounded-md border border-border bg-surface shadow-pop',
      )}
    >
      <div className="shrink-0 border-b border-border-soft px-3 py-2.5">
        <h3 className="text-[0.8125rem] font-semibold text-ink-900">Trier par</h3>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-1 p-2">
          {active.length === 0 ? (
            <p className="px-1 py-2 text-[0.8125rem] text-ink-400">Aucun critère</p>
          ) : (
            active.map((level, index) => {
              const label = catalogue.get(level.key)?.label ?? level.key;
              const direction = level.dir === 'asc' ? 'Croissant' : 'Décroissant';
              return (
                <div
                  key={level.key}
                  className="flex min-h-9 items-center gap-2 rounded-sm bg-surface-1 px-2 py-1"
                >
                  <span
                    aria-label={`Priorité ${index + 1}`}
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-pill bg-ink-900 text-[0.6875rem] font-semibold tabular-nums text-surface"
                  >
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1 break-words text-[0.8125rem] font-medium leading-snug text-ink-900">
                    {label}
                  </span>
                  <button
                    ref={(element) => {
                      if (element == null) directionRefs.current.delete(level.key);
                      else directionRefs.current.set(level.key, element);
                    }}
                    type="button"
                    aria-label={`${label} : ${direction}. Inverser l'ordre`}
                    onClick={() => onInvert(level.key)}
                    className={cn(
                      'flex h-8 shrink-0 items-center gap-1 rounded-sm border border-border bg-surface px-2',
                      'text-xs font-medium text-ink-700 transition-colors hover:border-border-strong hover:text-ink-900',
                      focus,
                    )}
                  >
                    <IconChevronDown
                      size={12}
                      className={cn('transition-transform', level.dir === 'asc' && 'rotate-180')}
                    />
                    {direction}
                  </button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Retirer le critère ${label}`}
                    title={`Retirer ${label}`}
                    onClick={() => {
                      pendingFocus.current = { action: 'removed', key: level.key };
                      onRemove(level.key);
                    }}
                    className={cn('w-8 shrink-0 px-0', focus)}
                  >
                    <IconClose size={12} />
                  </Button>
                </div>
              );
            })
          )}
        </div>

        <div className="border-t border-border-soft">
          <p className="px-3 pb-1.5 pt-2.5 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-ink-400">
            Ajouter un critère
          </p>
          <div className="px-2 pb-2">
            <Input autoFocus aria-label="Rechercher un champ à trier" placeholder="Rechercher un champ…" value={search} onChange={(event) => setSearch(event.target.value)} className="mb-1 h-8 rounded-sm px-2 text-[0.8125rem]" />
            {available.length === 0 ? (
              <p className="px-1 py-2 text-[0.8125rem] text-ink-400">
                {search ? 'Aucun champ' : 'Tous les champs sont utilisés'}
              </p>
            ) : (
              available.map((option) => (
                <button
                  key={option.key}
                  ref={(element) => {
                    if (element == null) availableRefs.current.delete(option.key);
                    else availableRefs.current.set(option.key, element);
                  }}
                  type="button"
                  onClick={() => {
                    pendingFocus.current = { action: 'added', key: option.key };
                    onToggle(option.key);
                    setSearch('');
                  }}
                  className={cn(
                    'flex min-h-8 w-full items-center rounded-sm px-2 py-1.5 text-left',
                    'text-[0.8125rem] font-medium leading-snug text-ink-700 transition-colors hover:bg-surface-1 hover:text-ink-900',
                    focus,
                  )}
                >
                  <span className="break-words">{option.label}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="shrink-0 border-t border-border-soft p-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={active.length === 0}
          onClick={() => {
            pendingFocus.current = { action: 'cleared' };
            onClear();
          }}
          className={cn('w-full justify-start px-2', focus)}
        >
          Ordre d'origine
        </Button>
      </div>
    </div>
  );
}

export default SortPopover;
